import moment from 'moment';

/**
 * PACI (Kuwait Mobile ID) helpers
 *
 * Generate QR response: { redirectUrl, requestId }
 * Poll response: { RequestId, Success, ResultCode, ResultMessage, CivilId, FullNameEn, FullNameAr, NationalityEn, BirthDate, ... }
 */

/** PACI returns the nationality as an ISO 3166 alpha-3 code, mapped to the nationality options (arrOptions) */
const NATIONALITY_CODES = {
  KWT: 'Kuwaiti',
  AFG: 'Afghanistan',
  DZA: 'Algerian',
  USA: 'American',
  ERI: 'Eritria',
  AUS: 'Australian',
  AUT: 'Austria',
  BHS: 'Bahamian',
  BHR: 'Bahrain',
  BGD: 'Bangladeshi',
  BEL: 'Belgium',
  BRA: 'Brazil',
  GBR: 'British',
  CAN: 'Canadian',
  CYM: 'Caymen Islands',
  CHN: 'China',
  CYP: 'Cypris',
  CZE: 'Czechs',
  DOM: 'Dominican',
  EGY: 'Egyptian',
  ARE: 'Emirates',
  PHL: 'Filipino',
  FIN: 'finland',
  FRA: 'French',
  DEU: 'Germany',
  HKG: 'Hong kong',
  HUN: 'Hungary',
  IND: 'Indian',
  IRN: 'Iranian',
  IRQ: 'Iraqi',
  IRL: 'Irland',
  ITA: 'Italy',
  JPN: 'Japan',
  JEY: 'Jersey',
  JOR: 'Jordanian',
  KOR: 'Korea',
  LBN: 'Lebanese',
  LBR: 'Liberia',
  LUX: 'luxomburg',
  MYS: 'Malaysia',
  MAR: 'Moroccan',
  NPL: 'Nepal',
  ANT: 'Netherlands Antilles',
  NLD: 'Netherlands',
  NZL: 'New Zealand',
  NGA: 'Nigerian',
  OMN: 'Oman',
  PAK: 'Pakistani',
  PSE: 'Palestinian',
  POL: 'Polland',
  PRT: 'Portuguese',
  QAT: 'Qatar',
  RUS: 'Russian',
  SAU: 'Saudi',
  SEN: 'Senegali',
  SGP: 'Singapore',
  SOM: 'Soumali',
  ESP: 'Spain',
  LKA: 'Sri Lanka',
  SDN: 'Sudanese',
  SWE: 'Swedian',
  CHE: 'Switzerland',
  SYR: 'Syrian',
  TUN: 'Tunisi',
  TUR: 'Turkish',
  VGB: 'Virgin Island',
  VIR: 'Virgin Island',
  YEM: 'Yamen',
};

/** Get the auth portal link (where the user scans the QR code) and requestId from the generate QR response */
const extractQrResponse = (res = {}) => ({
  redirectUrl: (res && res.redirectUrl) || '',
  requestId: (res && res.requestId) || '',
});

/** Use the nationality option label when the code is known, otherwise keep the PACI value */
const mapNationality = (value) => {
  if (!value) return '';
  return NATIONALITY_CODES[`${value}`.toUpperCase()] || `${value}`;
};

/** PACI birth date (e.g. 1986-05-09T00:00:00) to the DateInput format (DD/MM/YYYY) */
const mapBirthDate = (value) => {
  if (!value) return '';
  const date = moment(value, moment.ISO_8601, true);
  return date.isValid() ? date.format('DD/MM/YYYY') : '';
};

/** Map the PACI data to the ID & Passport and Client Details fields */
const mapPaciData = (data) => {
  const {
    CivilId, FullNameEn, FullNameAr, NationalityEn, BirthDate,
  } = data || {};
  return {
    civilId: CivilId ? `${CivilId}` : '',
    /** English name first, Arabic name if the English one is empty */
    fullName: FullNameEn || FullNameAr || '',
    nationality: mapNationality(NationalityEn),
    dateOfBirth: mapBirthDate(BirthDate),
  };
};

/** The backend has the PACI data once it returns the civil ID, or when PACI reported a failure */
const isPaciResultFinal = res => !!res && (!!res.CivilId || res.Success === false);

/** Callback URL that PACI will redirect to after the user approves on the mobile app */
const getCallbackUrl = path => `${window.location.origin}${(process.env.PUBLIC_URL || '').replace(/\/$/, '')}${path}`;

const paci = {
  extractQrResponse,
  mapPaciData,
  isPaciResultFinal,
  getCallbackUrl,
};

export default paci;
